import {
  AnalyticsEvent,
  AnalyticsEventName,
  AnalyticsEventProperties,
  filterUnsafeProperties,
} from '@ms/yammer-telemetry';
import { loadReportOneDSEvent } from '@ms/yammer-telemetry-1ds';
import { getTelemetryClientConfig } from '@ms/yammer-telemetry-store';

import { reportTelemetryEvent, reportTelemetryEventNow } from '../report';

interface ReportUsageOptions {
  /**
   * The event's name as defined in the AnalyticsEventName type. This will be logged in the event's
   * 'EventName' column.
   */
  readonly eventName: AnalyticsEventName;
  /**
   * An optional set of custom properties to log for the event. These properties must be defined in
   * an interface in the telemetry types file and added as part of the AnalyticsEventProperties type
   * (see the README about the process for adding new custom properties). Each value passed here will
   * be logged in a column named with the key within "properties" in the yamjsevents table.
   */
  readonly eventProperties?: AnalyticsEventProperties;
}

const getAnalyticsEvent = (options: ReportUsageOptions): AnalyticsEvent => {
  const { eventName, eventProperties = {} } = options;

  return {
    type: 'Analytics',
    name: eventName,
    occurredAt: Date.now().toString(),
    properties: filterUnsafeProperties<AnalyticsEventProperties>(eventName, eventProperties),
  };
};

const reportUsageTo1DS = async (analyticsEvent: AnalyticsEvent) => {
  const { type, name, properties } = analyticsEvent;
  const { reportOneDSEvent } = await loadReportOneDSEvent();

  reportOneDSEvent({
    baseType: type,
    name,
    data: properties,
  });
};

export type ReportUsage = (options: ReportUsageOptions) => void;
/**
 * @deprecated Use `reportAnalyticsEvent` instead.
 */
export const reportUsage: ReportUsage = (options) => {
  const analyticsEvent = getAnalyticsEvent(options);
  const { oneDSInstrumentationKey } = getTelemetryClientConfig();

  if (oneDSInstrumentationKey) {
    reportUsageTo1DS(analyticsEvent);

    return;
  }

  reportTelemetryEvent(analyticsEvent);
};

type ReportUsageNow = (options: ReportUsageOptions) => Promise<void>;
export const reportUsageNow: ReportUsageNow = async (options) => {
  const analyticsEvent = getAnalyticsEvent(options);
  const { oneDSInstrumentationKey } = getTelemetryClientConfig();

  if (oneDSInstrumentationKey) {
    reportUsageTo1DS(analyticsEvent);

    return;
  }

  await reportTelemetryEventNow(analyticsEvent);
};
